import React, { useCallback, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import RefreshControl from "@/components/RefreshControl";
import useAutoRefresh from "@/lib/useAutoRefresh";
import {
  CheckCircle, XCircle, CloudArrowUp, Broom,
  WarningCircle, Clock, ArrowClockwise, Trash, Skull,
} from "@phosphor-icons/react";

const STATUS_STYLES = {
  succeeded: { bg: "#D1FAE5", text: "#047857", border: "#10B981" },
  pending: { bg: "#FEF3C7", text: "#92400E", border: "#F59E0B" },
  failed: { bg: "#FEE2E2", text: "#991B1B", border: "#EF4444" },
  retrying: { bg: "#DBEAFE", text: "#1D4ED8", border: "#3B82F6" },
  dead_letter: { bg: "#FEF3C7", text: "#78350F", border: "#D97706" },
};

export default function Sync() {
  const { user } = useAuth();
  // Several Base44 admin endpoints (test connection, clear queue, discard
  // entries) are admin-only on the backend even though /sync is accessible
  // to managers. Hide the buttons managers can't use so they don't see a
  // confusing "Admin only" error after clicking. Managers retain read-only
  // access plus Run sync (which is allowed for them).
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  // Confirm modal — populated when an action needs explicit user OK. Uses
  // shadcn AlertDialog (in-DOM) instead of native window.confirm(), which is
  // silently blocked by sandboxed iframes / strict browser modes.
  const [confirmState, setConfirmState] = useState(null); // {title, body, onConfirm}
  const closeConfirm = () => setConfirmState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [s, q] = await Promise.all([
        api.get("/base44/status"),
        api.get("/base44/push-queue"),
      ]);
      setStatus(s.data);
      setQueue(q.data?.entries || []);
    } finally { setLoading(false); }
  }, []);

  const { refresh, refreshing, lastUpdatedAt } = useAutoRefresh(fetchAll);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const testConnection = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/base44/test");
      flash("success", `Connection OK · ${data?.detail || ""}`);
    } catch (err) {
      flash("error", formatApiErrorDetail(err?.response?.data?.detail) || "Test failed");
    } finally { setBusy(""); }
  };

  const runSync = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/base44/sync");
      flash("success", `Sync triggered · pulled ${data?.pulled ?? "?"} orders`);
      refresh();
    } catch (err) {
      flash("error", formatApiErrorDetail(err?.response?.data?.detail) || "Sync failed");
    } finally { setBusy(""); }
  };

  const clearSucceeded = () => setConfirmState({
    title: "Clear succeeded entries?",
    body: "This removes all successful entries from the push queue. Failed and dead-letter entries are untouched.",
    confirmLabel: "Clear succeeded",
    onConfirm: async () => {
      setBusy("clear");
      try {
        const { data } = await api.post("/base44/push-queue/clear-succeeded");
        flash("success", `Cleared ${data?.removed ?? ""} successful entries`);
        refresh();
      } catch (err) {
        flash("error", formatApiErrorDetail(err?.response?.data?.detail) || "Clear failed");
      } finally { setBusy(""); }
    },
  });

  const clearDeadLetters = () => setConfirmState({
    title: "Clear all dead-letter entries?",
    body: "Dead-letter entries have exhausted retries and won't push to Base44 again. Removing them is permanent.",
    confirmLabel: "Clear dead letters",
    destructive: true,
    onConfirm: async () => {
      setBusy("clear-dl");
      try {
        const { data } = await api.post("/base44/push-queue/clear-dead-letter");
        flash("success", `Cleared ${data?.removed ?? ""} dead-letter entries`);
        refresh();
      } catch (err) {
        flash("error", formatApiErrorDetail(err?.response?.data?.detail) || "Clear failed");
      } finally { setBusy(""); }
    },
  });

  const discardEntry = (entry) => setConfirmState({
    title: `Discard this ${entry.status.replace("_", " ")} entry?`,
    body: `Permanently remove the push queue entry for ${entry.external_order_id}.`,
    confirmLabel: "Discard",
    destructive: true,
    onConfirm: async () => {
      setBusy(`discard-${entry.id}`);
      try {
        await api.delete(`/base44/push-queue/${entry.id}`);
        flash("success", `Discarded entry for ${entry.external_order_id}`);
        refresh();
      } catch (err) {
        flash("error", formatApiErrorDetail(err?.response?.data?.detail) || "Discard failed");
      } finally { setBusy(""); }
    },
  });

  const retryEntry = async (entry) => {
    setBusy(`retry-${entry.id}`);
    try {
      await api.post(`/base44/push-queue/${entry.id}/retry`);
      flash("success", `Retry queued for ${entry.external_order_id}`);
      refresh();
    } catch (err) {
      flash("error", formatApiErrorDetail(err?.response?.data?.detail) || "Retry failed");
    } finally { setBusy(""); }
  };

  const counts = queue.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="sync-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Admin</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Base44 sync</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
            Monitor push queue, retry failed updates, manually pull from Base44.
          </p>
        </div>
        <RefreshControl
          onRefresh={refresh}
          refreshing={refreshing}
          lastUpdatedAt={lastUpdatedAt}
          testId="sync-refresh"
        />
      </div>

      {msg && (
        <div
          data-testid="sync-feedback"
          className={`px-4 py-3 rounded-md text-sm font-semibold ${
            msg.type === "success"
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {msg.text}
        </div>
      )}

      {!isAdmin && (
        <div
          data-testid="sync-manager-banner"
          className="px-4 py-3 rounded-md text-xs text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700"
        >
          You can monitor the push queue and trigger a manual sync. Connection testing and queue cleanup are admin-only.
        </div>
      )}

      {/* Status + actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border-zinc-200 dark:border-zinc-700" data-testid="status-card">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Connection</div>
          <div className="mt-2 flex items-center gap-2">
            {status?.configured ? (
              <><CheckCircle size={22} weight="fill" className="text-emerald-600" /><span className="font-heading font-bold text-lg">Configured</span></>
            ) : (
              <><XCircle size={22} weight="fill" className="text-red-600" /><span className="font-heading font-bold text-lg">Not configured</span></>
            )}
          </div>
          {isAdmin && (
            <Button onClick={testConnection} disabled={busy === "test"} variant="outline" className="mt-4 w-full" data-testid="test-connection">
              {busy === "test" ? "Testing…" : "Test connection"}
            </Button>
          )}
        </Card>

        <Card className="p-5 border-zinc-200 dark:border-zinc-700" data-testid="pull-card">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Manual sync</div>
          <div className="mt-2 font-heading font-bold text-lg">Pull from Base44</div>
          <p className="text-xs text-zinc-500 mt-1">Re-fetches all orders &amp; items.</p>
          <Button onClick={runSync} disabled={busy === "sync"} className="bg-[#C8102E] hover:bg-[#A00D25] mt-4 w-full" data-testid="run-sync">
            <CloudArrowUp size={16} weight="bold" className="mr-1.5" />
            {busy === "sync" ? "Syncing…" : "Run sync"}
          </Button>
        </Card>

        <Card className="p-5 border-zinc-200 dark:border-zinc-700" data-testid="queue-card">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Push queue</div>
          <div className="mt-2 font-heading font-bold text-lg">{queue.length} entries</div>
          <div className="mt-1 text-xs text-zinc-500 flex flex-wrap gap-x-3 gap-y-0.5">
            {counts.succeeded ? <span>✓ {counts.succeeded} ok</span> : null}
            {counts.pending ? <span className="text-amber-700">◷ {counts.pending} pending</span> : null}
            {counts.failed ? <span className="text-red-700">✕ {counts.failed} failed</span> : null}
            {counts.dead_letter ? <span className="text-amber-800 dark:text-amber-300">☠ {counts.dead_letter} dead</span> : null}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2">
            {isAdmin && (
              <Button onClick={clearSucceeded} disabled={busy === "clear" || !counts.succeeded} variant="outline" className="w-full" data-testid="clear-succeeded">
                <Broom size={16} weight="bold" className="mr-1.5" />
                Clear succeeded
              </Button>
            )}
            {isAdmin && (
              <Button onClick={clearDeadLetters} disabled={busy === "clear-dl" || !counts.dead_letter} variant="outline" className="w-full text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/40" data-testid="clear-dead-letters">
                <Skull size={16} weight="bold" className="mr-1.5" />
                Clear dead letters
              </Button>
            )}
          </div>
        </Card>
      </div>

      {/* Queue list */}
      <Card className="border-zinc-200 dark:border-zinc-700 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
          <h2 className="font-heading font-bold text-base">Push queue entries</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-zinc-500">Loading…</div>
        ) : queue.length === 0 ? (
          <div className="p-8 text-center text-zinc-500" data-testid="queue-empty">
            Nothing in the push queue. The backend is keeping Base44 in sync.
          </div>
        ) : (
          <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {queue.map((e) => (
              <QueueEntry key={e.id} entry={e} busy={busy} onRetry={retryEntry} onDiscard={discardEntry} canManage={isAdmin} />
            ))}
          </div>
        )}
      </Card>

      <AlertDialog open={!!confirmState} onOpenChange={(o) => { if (!o) closeConfirm(); }}>
        <AlertDialogContent data-testid="sync-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmState?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sync-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="sync-confirm-ok"
              className={confirmState?.destructive ? "bg-[#C8102E] hover:bg-[#A00D25] text-white" : undefined}
              onClick={() => {
                const fn = confirmState?.onConfirm;
                closeConfirm();
                if (fn) fn();
              }}
            >
              {confirmState?.confirmLabel || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function QueueEntry({ entry, busy, onRetry, onDiscard, canManage }) {
  const s = STATUS_STYLES[entry.status] || STATUS_STYLES.pending;
  const label = entry.status === "dead_letter" ? "dead letter" : entry.status;
  return (
    <div className="px-5 py-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-center" data-testid={`queue-entry-${entry.id}`}>
      <div className="md:col-span-2">
        <span
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold rounded-md px-2 py-1 border"
          style={{ background: s.bg, color: s.text, borderColor: s.border }}
        >
          {entry.status === "succeeded" && <CheckCircle size={11} weight="fill" />}
          {entry.status === "failed" && <WarningCircle size={11} weight="fill" />}
          {entry.status === "pending" && <Clock size={11} weight="fill" />}
          {entry.status === "dead_letter" && <Skull size={11} weight="fill" />}
          {label}
        </span>
        <div className="text-[10px] font-mono text-zinc-500 mt-1">{entry.attempts || 0} attempt{entry.attempts === 1 ? "" : "s"}</div>
      </div>
      <div className="md:col-span-4 min-w-0">
        <div className="font-mono text-xs font-semibold truncate">{entry.external_order_id}</div>
        <div className="text-xs text-zinc-600 dark:text-zinc-300 truncate">{entry.reason}</div>
      </div>
      <div className="md:col-span-3 min-w-0 text-xs text-zinc-600 dark:text-zinc-300">
        <div className="font-semibold">→ {entry.new_status || "—"}</div>
        {entry.item_updates?.length ? (
          <div className="font-mono text-[10px] truncate">{entry.item_updates.length} item updates</div>
        ) : null}
      </div>
      <div className="md:col-span-2 text-[10px] text-zinc-400 font-mono">
        {entry.last_attempt_at ? new Date(entry.last_attempt_at).toLocaleString() : "—"}
        {entry.last_error && (
          <div className="text-red-700 truncate font-sans not-italic mt-0.5" title={entry.last_error}>{entry.last_error}</div>
        )}
      </div>
      <div className="md:col-span-1 flex justify-end gap-1">
        {canManage && entry.status === "failed" && (
          <Button size="sm" variant="outline" onClick={() => onRetry(entry)} disabled={busy === `retry-${entry.id}`} className="h-8 px-2" data-testid={`retry-${entry.id}`} title="Retry">
            <ArrowClockwise size={14} weight="bold" />
          </Button>
        )}
        {canManage && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDiscard(entry)}
            disabled={busy === `discard-${entry.id}`}
            className="h-8 px-2 text-red-700 dark:text-red-400 border-zinc-200 dark:border-zinc-700 hover:bg-red-50 dark:hover:bg-red-950/40"
            data-testid={`discard-${entry.id}`}
            title="Discard entry"
          >
            <Trash size={14} weight="bold" />
          </Button>
        )}
      </div>
    </div>
  );
}
